import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AccessControlService } from '../services/access-control.service';
import { AuthService } from '../services/auth.service';
import { AppModule, AccessLevel } from '../models/access-control.model';

/**
 * Guard that checks if user has required access level for a module.
 *
 * Usage in routes:
 * ```
 * {
 *   path: 'members',
 *   component: AccountsComponent,
 *   canActivate: [moduleAccessGuard],
 *   data: { module: 'tenant.members', requiredLevel: 'view' }
 * }
 * ```
 *
 * If requiredLevel is not specified, defaults to 'view'.
 */
export const moduleAccessGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const accessControl = inject(AccessControlService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for initial role sync to complete before checking access
  await authService.initReady();

  // Handle case where roles aren't loaded yet - redirect to login
  if (accessControl.getUserRoles().length === 0) {
    return router.createUrlTree(['/launcher'], {
      queryParams: { returnUrl: state.url }
    });
  }

  // Get module and required level from route data
  const module = route.data['module'] as AppModule | undefined;
  const modules = route.data['modules'] as AppModule[] | undefined;
  const requiredLevel = (route.data['requiredLevel'] as AccessLevel) || 'view';

  // If no module(s) specified, allow access (misconfigured route)
  if (!module && !modules?.length) {
    return true;
  }

  // Check required access on the single module or ANY of the list. `modules`
  // (any-of) lets a route be reached from either scope — e.g. dashboard admin
  // open to both tenant and chapter admins.
  if (module && accessControl.hasAccess(module, requiredLevel)) {
    return true;
  }
  if (modules?.some(m => accessControl.hasAccess(m, requiredLevel))) {
    return true;
  }

  if (module && isReportModule(module)) {
    const reportRoute = findAccessibleReportRoute(accessControl);
    if (reportRoute) {
      return router.createUrlTree([reportRoute]);
    }
  }

  // Access denied - find a module the user CAN access, or show error page
  const fallbackRoute = findAccessibleRoute(accessControl);
  if (fallbackRoute) {
    return router.createUrlTree([fallbackRoute]);
  }

  // No accessible modules - redirect to error page
  return router.createUrlTree(['/error'], {
    queryParams: { reason: 'no-access' }
  });
};

export const reportRedirectGuard: CanActivateFn = async (_route, state) => {
  const accessControl = inject(AccessControlService);
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.initReady();

  if (accessControl.getUserRoles().length === 0) {
    return router.createUrlTree(['/launcher'], {
      queryParams: { returnUrl: state.url }
    });
  }

  const reportRoute = findAccessibleReportRoute(accessControl);
  if (reportRoute) {
    return router.createUrlTree([reportRoute]);
  }

  const fallbackRoute = findAccessibleRoute(accessControl);
  if (fallbackRoute) {
    return router.createUrlTree([fallbackRoute]);
  }

  return router.createUrlTree(['/error'], {
    queryParams: { reason: 'no-access' }
  });
};

/**
 * Find a route the user has access to, for fallback navigation
 */
function findAccessibleRoute(accessControl: AccessControlService): string | null {
  // Priority order for fallback routes
  const fallbackModules: { module: AppModule; route: string }[] = [
    { module: 'tenant.dashboard', route: '/dashboard' },
    { module: 'tenant.memberships', route: '/memberships/accounts' },
    { module: 'tenant.chapters', route: '/chapters' },
    { module: 'chapter.pipelines', route: '/engagement/pipelines' },
    { module: 'chapter.tasks', route: '/engagement/work-queue' },
    { module: 'chapter.activities', route: '/engagement/activities' },
    { module: 'chapter.resources', route: '/resources' },
    { module: 'chapter.events', route: '/events' },
    { module: 'tenant.membership-report', route: '/reports/membership' },
    { module: 'tenant.events-report', route: '/reports/events' },
    { module: 'tenant.financials-report', route: '/reports/financials' },
  ];

  for (const { module, route } of fallbackModules) {
    if (accessControl.hasAccess(module, 'view')) {
      return route;
    }
  }

  return null;
}

function findAccessibleReportRoute(accessControl: AccessControlService): string | null {
  const reportRoutes: { module: AppModule; route: string }[] = [
    { module: 'tenant.membership-report', route: '/reports/membership' },
    { module: 'tenant.events-report', route: '/reports/events' },
    { module: 'tenant.financials-report', route: '/reports/financials' },
  ];

  for (const { module, route } of reportRoutes) {
    if (accessControl.hasAccess(module, 'view')) {
      return route;
    }
  }

  return null;
}

function isReportModule(module: AppModule): boolean {
  return (
    module === 'tenant.membership-report' ||
    module === 'tenant.events-report' ||
    module === 'tenant.financials-report'
  );
}

/**
 * Factory function to create a guard for a specific module.
 * Useful for inline route definitions.
 *
 * Usage:
 * ```
 * {
 *   path: 'members',
 *   component: AccountsComponent,
 *   canActivate: [requireModuleAccess('tenant.members')]
 * }
 * ```
 */
export function requireModuleAccess(module: AppModule, requiredLevel: AccessLevel = 'view'): CanActivateFn {
  return async (_route, state) => {
    const accessControl = inject(AccessControlService);
    const authService = inject(AuthService);
    const router = inject(Router);

    // Wait for initial role sync to complete before checking access
    await authService.initReady();

    // Handle case where roles aren't loaded yet - redirect to login
    if (accessControl.getUserRoles().length === 0) {
      return router.createUrlTree(['/launcher'], {
        queryParams: { returnUrl: state.url }
      });
    }

    if (accessControl.hasAccess(module, requiredLevel)) {
      return true;
    }

    const fallbackRoute = findAccessibleRoute(accessControl);
    if (fallbackRoute) {
      return router.createUrlTree([fallbackRoute]);
    }

    return router.createUrlTree(['/error'], {
      queryParams: { reason: 'no-access' }
    });
  };
}
